import { useMemo } from 'react'
import {
  ScheduleAssignment,
  ScheduleUser,
  addDays,
  endOfMonth,
  formatDateISO,
  isWeekend,
  pickTextColor,
  startOfMonth,
  userInitials,
  WEEKDAYS_PL,
} from './utils'

interface Props {
  anchor: Date
  assignments: ScheduleAssignment[]
  users: ScheduleUser[]
  canManage: boolean
  currentUserId: number | null
  onEdit: (a: ScheduleAssignment) => void
  onAdd: (defaultDate?: string, defaultUserId?: number) => void
  onDelete: (id: number) => void
}

export default function ScheduleUserMatrixView({
  anchor, assignments, users, canManage, currentUserId, onEdit, onAdd,
}: Props) {
  const monthStart = useMemo(() => startOfMonth(anchor), [anchor])
  const monthEnd = useMemo(() => endOfMonth(anchor), [anchor])
  const days = useMemo(() => {
    const out: Date[] = []
    let cur = monthStart
    while (cur.getTime() <= monthEnd.getTime()) {
      out.push(cur)
      cur = addDays(cur, 1)
    }
    return out
  }, [monthStart, monthEnd])

  // user_id -> date -> [assignment]
  const matrix = useMemo(() => {
    const m: Record<number, Record<string, ScheduleAssignment[]>> = {}
    for (const a of assignments) {
      if (!m[a.user_id]) m[a.user_id] = {}
      if (!m[a.user_id][a.date]) m[a.user_id][a.date] = []
      m[a.user_id][a.date].push(a)
    }
    return m
  }, [assignments])

  const today = formatDateISO(new Date())

  // Pokaż tylko osoby, które mają wpisy lub są przypisane do referatu
  const usersToShow = useMemo(() => {
    const ids = new Set<number>(users.map(u => u.id))
    for (const a of assignments) ids.add(a.user_id)
    const merged: ScheduleUser[] = users.slice()
    for (const a of assignments) {
      if (!merged.find(u => u.id === a.user_id)) {
        merged.push({ id: a.user_id, username: a.user_username || `user${a.user_id}`, full_name: a.user_name })
      }
    }
    return merged
      .filter(u => ids.has(u.id))
      .sort((x, y) => (x.full_name || x.username || '').localeCompare(y.full_name || y.username || ''))
  }, [users, assignments])

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="sticky left-0 z-20 bg-gray-50 px-2 py-1.5 text-left font-semibold text-gray-700 border-r border-gray-200 min-w-[160px]">
              Osoba
            </th>
            {days.map(d => {
              const iso = formatDateISO(d)
              const isToday = iso === today
              const dow = (d.getDay() + 6) % 7
              return (
                <th
                  key={iso}
                  className={`px-1 py-1 text-center font-semibold border-r last:border-r-0 border-gray-200 ${
                    isWeekend(d) ? 'bg-gray-100' : ''
                  } ${isToday ? 'bg-blue-100 text-blue-700' : 'text-gray-600'}`}
                >
                  <div className="text-[10px]">{WEEKDAYS_PL[dow]}</div>
                  <div className="text-[11px]">{d.getDate()}</div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {usersToShow.length === 0 && (
            <tr>
              <td colSpan={days.length + 1} className="text-center text-gray-400 italic p-4">
                Brak użytkowników w tym referacie
              </td>
            </tr>
          )}
          {usersToShow.map(u => (
            <tr key={u.id} className="border-t border-gray-200 hover:bg-gray-50/60">
              <td className={`sticky left-0 z-10 bg-white px-2 py-1 border-r border-gray-200 ${
                u.id === currentUserId ? 'font-semibold text-yellow-700' : 'text-gray-800'
              }`}>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[10px] font-medium">
                    {userInitials(u)}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate">{u.full_name || u.username}</div>
                    {u.role && <div className="text-[10px] text-gray-400">{u.role}</div>}
                  </div>
                </div>
              </td>
              {days.map(d => {
                const iso = formatDateISO(d)
                const items = (matrix[u.id] && matrix[u.id][iso]) || []
                return (
                  <td
                    key={iso}
                    className={`p-0.5 border-r last:border-r-0 border-gray-200 align-top ${
                      isWeekend(d) ? 'bg-gray-50' : ''
                    }`}
                    style={{ minWidth: 32 }}
                  >
                    <div className="flex flex-col gap-0.5">
                      {items.map(a => (
                        <MatrixCell key={a.id} a={a} onEdit={onEdit} />
                      ))}
                      {items.length === 0 && canManage && (
                        <button
                          onClick={() => onAdd(iso, u.id)}
                          className="text-[10px] text-gray-300 hover:text-blue-600 py-0.5"
                          title="Dodaj"
                        >+</button>
                      )}
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MatrixCell({ a, onEdit }: { a: ScheduleAssignment, onEdit: (a: ScheduleAssignment) => void }) {
  const bg = a.effective_color || '#3b82f6'
  const fg = pickTextColor(bg)
  const isOnCall = a.effective_category === 'oncall'
  const isLeave = a.effective_category === 'leave'
  // Krótka etykieta - 1-3 znaki
  const short = (a.effective_label || '').slice(0, 3).toUpperCase()

  return (
    <button
      onClick={() => onEdit(a)}
      className={`rounded text-center px-1 py-0.5 leading-tight ${isOnCall ? 'border border-dashed' : ''}`}
      style={{
        backgroundColor: isLeave ? `${bg}22` : bg,
        borderColor: bg,
        color: isLeave ? '#374151' : fg,
      }}
      title={`${a.user_name || ''} ${a.effective_label}${a.note ? ` – ${a.note}` : ''}`}
    >
      <span className="text-[10px] font-semibold">{short}</span>
    </button>
  )
}
